import { Link, useLocation } from "react-router";

import { cx } from "@/lib/cx";
import { contextFromSearch, levelForPath, levelTargets, type LevelContext } from "@/lib/portfolioLevels";

import "@/components/ui/Segmented.css";
import "./LevelBar.css";

export type LevelBarItem = {
  key: string;
  label: string;
  active: boolean;
  /** Een link; zonder `to` is het een knop met `onSelect`. */
  to?: string;
  onSelect?: () => void;
  title?: string;
  disabled?: boolean;
};

/**
 * De niveaubalk (FB-110): de segmentknop van stijl A, met het niveau waar je
 * zit in Q-Estate-blauw in plaats van de witte pil van een gewone schakelaar.
 * Jan: "graag de keuze oplichten (of blauw kleuren) zodat je weet dat je daar
 * zit".
 *
 * Alleen de vorm; wat een niveau doet bepaalt de aanroeper. Vastgoed geeft
 * links mee (`PortfolioLevelBar`), een scherm dat binnen zichzelf van niveau
 * wisselt (de lifecycle, FB-115) knoppen met `onSelect`.
 */
export function LevelBar({ items, label }: { items: LevelBarItem[]; label: string }) {
  return (
    <nav className="atl-seg atl-levelbar" aria-label={label}>
      {items.map((item) => {
        const className = cx("atl-seg__item", "atl-levelbar__item", item.active && "is-active");
        return item.to !== undefined ? (
          <Link
            key={item.key}
            to={item.to}
            className={className}
            aria-current={item.active ? "page" : undefined}
            title={item.title}
          >
            {item.label}
          </Link>
        ) : (
          <button
            key={item.key}
            type="button"
            className={className}
            aria-pressed={item.active}
            onClick={item.onSelect}
            disabled={item.disabled}
            title={item.title}
          >
            {item.label}
          </button>
        );
      })}
    </nav>
  );
}

/**
 * Portefeuille, deelportefeuille, complex, pand en object op elk scherm van
 * Vastgoed, ook op een detailscherm. De context (welk complex, welk pand) komt
 * uit de querystring van een lijst, en een detailscherm vult hem aan met wat
 * het zelf weet (`context`).
 */
export function PortfolioLevelBar({ context }: { context?: LevelContext }) {
  const { pathname, search, hash } = useLocation();
  const vanUrl = contextFromSearch(search);
  const samen: LevelContext = {
    complex: context?.complex ?? vanUrl.complex,
    buildingKey: context?.buildingKey ?? vanUrl.buildingKey,
    buildingId: context?.buildingId ?? null,
  };
  const items = levelTargets(levelForPath(pathname, hash), samen, `${pathname}${search}${hash}`).map((target) => ({
    key: target.level,
    label: target.label,
    active: target.active,
    to: target.to,
    title: target.title,
  }));
  return <LevelBar items={items} label="Niveau in de portefeuille" />;
}
